import React, { Suspense } from 'react';
import { connect } from 'react-redux';
import { Route, Routes, Navigate } from 'react-router-dom';
import AppLayout from './layout/AppLayout';
import { WebRTCProvider } from './context/WebRTCContext';
import { ScreenShareProvider } from './context/ScreenShareContext';
import { SocketProvider } from './context/SocketContext';
import { PeopleProvider } from './context/PeopleContext';
import { KeywordProvider } from './context/KeywordContext';
import { AIModeProvider } from './context/AIModeContext';
import { AdvisorQueueProvider } from './context/AdvisorQueueContext';
import { ProtectedRoute } from './helpers/authHelper';
import { ViewErrorBoundaryOutlet } from './components/ViewErrorBoundary';
import TooltipProvider from './components/TooltipProvider';

// Lazy load views - Auth
const Login = React.lazy(() => import('./views/user/Login'));
const Register = React.lazy(() => import('./views/user/Register'));
const ForgotPassword = React.lazy(() => import('./views/user/ForgotPassword'));
const VerifyEmail = React.lazy(() => import('./views/user/VerifyEmail'));

// Lazy load views - Monitoring
const FileBrowser = React.lazy(() => import('./views/system/FileBrowser'));
const Dossier = React.lazy(() => import('./views/monitoring/Dossier'));
const SacredTimeline = React.lazy(() => import('./views/monitoring/SacredTimeline'));
const Screen = React.lazy(() => import('./views/monitoring/Screen'));
const ExposureDashboard = React.lazy(() => import('./views/monitoring/ExposureDashboard'));
const MediaGallery = React.lazy(() => import('./views/monitoring/MediaGallery'));
const DomainHeatmap = React.lazy(() => import('./views/monitoring/DomainHeatmap'));
const KeywordDashboard = React.lazy(() => import('./views/monitoring/KeywordDashboard'));
const TabSurveillance = React.lazy(() => import('./views/monitoring/TabSurveillance'));

// Lazy load views - Punishment
const TimeLimit = React.lazy(() => import('./views/punishment/TimeLimit'));
const InputSabotage = React.lazy(() => import('./views/punishment/InputSabotage'));
const Economy = React.lazy(() => import('./views/punishment/Economy'));
const SpinWheel = React.lazy(() => import('./views/punishment/SpinWheel'));
const WriteLines = React.lazy(() => import('./views/punishment/WriteLines'));
const Quiz = React.lazy(() => import('./views/punishment/Quiz'));
const WordSearch = React.lazy(() => import('./views/games/WordSearch'));
const EyeLock = React.lazy(() => import('./views/games/EyeLock'));
const SoundBlaster = React.lazy(() => import('./views/punishment/SoundBlaster'));
const WindowRearrange = React.lazy(() => import('./views/punishment/WindowRearrange'));
const CountdownTimer = React.lazy(() => import('./views/punishment/CountdownTimer'));
const PhantomCursor = React.lazy(() => import('./views/punishment/PhantomCursor'));
const WallpaperHijack = React.lazy(() => import('./views/punishment/WallpaperHijack'));
const ScreenTint = React.lazy(() => import('./views/punishment/ScreenTint'));
const AuctionHouse = React.lazy(() => import('./views/punishment/AuctionHouse'));

// Lazy load views - Integrations
const PiShock = React.lazy(() => import('./views/integrations/PiShock'));
const GazeTracking = React.lazy(() => import('./views/integrations/GazeTracking'));
const Anthropic = React.lazy(() => import('./views/integrations/Anthropic'));
const CameraMonitor = React.lazy(() => import('./views/integrations/CameraMonitor'));

// Lazy load views - Communication
const Chat = React.lazy(() => import('./views/communication/Chat'));
const AIVoice = React.lazy(() => import('./views/communication/AIVoice'));
const QuickConfession = React.lazy(() => import('./views/communication/QuickConfession'));
const Journal = React.lazy(() => import('./views/communication/Journal'));
const UserPanel = React.lazy(() => import('./views/communication/UserPanel'));
const ForcedBrowser = React.lazy(() => import('./views/communication/ForcedBrowser'));

// Lazy load views - Games
const Games = React.lazy(() => import('./views/games/Games'));
const Labyrinth = React.lazy(() => import('./views/games/Labyrinth'));
const Challenges = React.lazy(() => import('./views/games/Challenges'));
const TaskBoard = React.lazy(() => import('./views/games/TaskBoard'));
const Siege = React.lazy(() => import('./views/siege/SiegeControl'));
const CardWar = React.lazy(() => import('./views/games/CardWar'));
const Blackjack = React.lazy(() => import('./views/games/Blackjack'));
const Battleship = React.lazy(() => import('./views/games/Battleship'));
const DungeonCrawler = React.lazy(() => import('./views/games/DungeonCrawler'));
const GoFish = React.lazy(() => import('./views/games/GoFish'));
const Hangman = React.lazy(() => import('./views/games/Hangman'));
const Chess = React.lazy(() => import('./views/games/Chess'));
const MemoryMatch = React.lazy(() => import('./views/games/MemoryMatch'));
const Jigsaw = React.lazy(() => import('./views/games/Jigsaw'));
const InterrogationControl = React.lazy(() => import('./views/interrogation/InterrogationControl'));

// Mobile device views removed from sidebar

// Lazy load views - AI
const AICockpit = React.lazy(() => import('./views/ai/AICockpit'));

// Lazy load views - Home (person-centric landing) + verb landings (four-verb overhaul)
const Home = React.lazy(() => import('./views/Home'));
const VerbLanding = React.lazy(() => import('./views/VerbLanding'));

// Lazy load views - System
const Configuration = React.lazy(() => import('./views/system/Configuration'));
const DebugSettings = React.lazy(() => import('./views/system/DebugSettings'));
const NotFound = React.lazy(() => import('./views/system/NotFound'));
const Unauthorized = React.lazy(() => import('./views/system/Unauthorized'));

// Loading fallback for lazy-loaded components
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#f5f4f2'
  }}>
    <div style={{
      width: '32px',
      height: '32px',
      // Use the app accent (lilac) — this spinner is the very first thing the
      // user sees, and it was hardcoded magenta (#922c88), a different brand
      // colour from the lilac accent everything else uses.
      border: '3px solid var(--accent-border, rgba(159, 140, 199, 0.2))',
      borderTopColor: 'var(--accent, #9f8cc7)',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
    <style>{`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

class App extends React.Component {
  render() {
    return (
      <TooltipProvider>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Auth Routes - Public */}
          <Route path="/user/login" element={<Login />} />
          <Route path="/user/register" element={<Register />} />
          <Route path="/user/forgot-password" element={<ForgotPassword />} />
          <Route path="/user/verify-email" element={<VerifyEmail />} />

          {/* Protected App Routes */}
          <Route
            path="/app/*"
            element={
              <ProtectedRoute
                element={
                  <WebRTCProvider>
                    <AIModeProvider>
                    <AdvisorQueueProvider>
                    <KeywordProvider>
                      <SocketProvider>
                        <PeopleProvider>
                        <ScreenShareProvider>
                        <div className="h-100">
                          <Routes>
                            <Route path="/" element={<AppLayout />}>
                              <Route element={<ViewErrorBoundaryOutlet />}>
                                {/* Home — person-centric landing (Watch) */}
                                <Route path="home" element={<Home />} />
                                {/* Verb landings — titles/subtitles come from NAV_VERBS
                                    (the single source of truth) so the sidebar, these
                                    landing pages, and the Home tiles all say the same
                                    thing: Interrogate / Investigate / Inflict / Configure. */}
                                <Route path="interrogate" element={<VerbLanding verbId="question" />} />
                                <Route path="do" element={<VerbLanding verbId="do" />} />
                                <Route path="look-back" element={<VerbLanding verbId="back" />} />
                                <Route path="set-up" element={<VerbLanding verbId="setup" />} />

                                {/* AI Cockpit */}
                                <Route path="ai/cockpit" element={<AICockpit />} />

                                {/* Monitoring Routes */}
                                <Route path="monitoring/screen" element={<Screen />} />
                                <Route path="monitoring/sacred-timeline" element={<SacredTimeline />} />
                                <Route path="monitoring/timeline" element={<Navigate to="/app/monitoring/sacred-timeline" replace />} />
                                <Route path="monitoring/file-browser" element={<FileBrowser />} />
                                <Route path="monitoring/dossier" element={<Dossier />} />
                                <Route path="monitoring/exposure" element={<ExposureDashboard />} />
                                <Route path="monitoring/media" element={<MediaGallery />} />
                                <Route path="monitoring/domain-heatmap" element={<DomainHeatmap />} />
                                {/* Removed URL-only duplicate pages — redirect to the surviving surfaces.
                                    Keywords → the Keyword Flags dashboard (which already owns group
                                    management); Shadow Browser → Tab Surveillance (its live successor). */}
                                <Route path="monitoring/keywords" element={<Navigate to="/app/monitoring/keyword-dashboard" replace />} />
                                <Route path="monitoring/shadow-browser" element={<Navigate to="/app/monitoring/tab-surveillance" replace />} />
                                <Route path="monitoring/keyword-dashboard" element={<KeywordDashboard />} />
                                <Route path="monitoring/tab-surveillance" element={<TabSurveillance />} />
                                <Route path="monitoring/time-limit" element={<TimeLimit />} />
                                {/* Redirect old punishment route */}
                                <Route path="punishment/time-limit" element={<Navigate to="/app/monitoring/time-limit" replace />} />

                                {/* Punishment Routes */}
                                <Route path="punishment/input-sabotage" element={<InputSabotage />} />
                                <Route path="punishment/economy" element={<Economy />} />
                                <Route path="punishment/spin-wheel" element={<SpinWheel />} />
                                <Route path="punishment/write-lines" element={<WriteLines />} />
                                <Route path="punishment/sound-blaster" element={<SoundBlaster />} />
                                <Route path="punishment/window-rearrange" element={<WindowRearrange />} />
                                <Route path="punishment/countdown-timer" element={<CountdownTimer />} />
                                <Route path="punishment/phantom-cursor" element={<PhantomCursor />} />
                                <Route path="punishment/wallpaper-hijack" element={<WallpaperHijack />} />
                                <Route path="punishment/quiz" element={<Quiz />} />
                                <Route path="punishment/screen-tint" element={<ScreenTint />} />
                                <Route path="punishment/auction-house" element={<AuctionHouse />} />

                                {/* Integration Routes */}
                                <Route path="integrations/pishock" element={<PiShock />} />
                                <Route path="integrations/gaze-tracking" element={<GazeTracking />} />
                                <Route path="integrations/anthropic" element={<Anthropic />} />
                                <Route path="integrations/cameras" element={<CameraMonitor />} />

                                {/* Legacy redirect for PiShock */}
                                <Route path="punishment/pishock" element={<Navigate to="/app/integrations/pishock" replace />} />

                                {/* Communication Routes */}
                                <Route path="communication/chat" element={<Chat />} />
                                <Route path="communication/interrogation" element={<InterrogationControl />} />
                                <Route path="communication/quick-confession" element={<QuickConfession />} />

                                {/* Voice Studio */}
                                <Route path="communication/ai-voice" element={<AIVoice />} />
                                <Route path="communication/journal" element={<Journal />} />
                                <Route path="communication/user-panel" element={<UserPanel />} />
                                <Route path="communication/forced-browser" element={<ForcedBrowser />} />

                                {/* Games Routes */}
                                <Route path="games" element={<Games />} />
                                <Route path="games/labyrinth" element={<Labyrinth />} />
                                <Route path="games/siege" element={<Siege />} />
                                <Route path="games/task-board" element={<TaskBoard />} />
                                <Route path="games/card-war" element={<CardWar />} />
                                <Route path="games/blackjack" element={<Blackjack />} />
                                <Route path="games/dungeon-crawler" element={<DungeonCrawler />} />
                                <Route path="games/battleship" element={<Battleship />} />
                                <Route path="games/go-fish" element={<GoFish />} />
                                <Route path="games/hangman" element={<Hangman />} />
                                <Route path="games/chess" element={<Chess />} />
                                <Route path="games/memory-match" element={<MemoryMatch />} />
                                <Route path="games/jigsaw" element={<Jigsaw />} />
                                <Route path="games/challenges" element={<Challenges />} />
                                <Route path="games/word-search" element={<WordSearch />} />
                                <Route path="games/*" element={<Games />} />

                                <Route path="punishment/eye-lock" element={<EyeLock />} />

                                {/* Legacy redirects for moved features */}
                                <Route path="punishment/challenges" element={<Navigate to="/app/games/challenges" replace />} />
                                <Route path="punishment/word-search" element={<Navigate to="/app/games/word-search" replace />} />
                                <Route path="games/eye-lock" element={<Navigate to="/app/punishment/eye-lock" replace />} />
                                <Route path="games/interrogation" element={<Navigate to="/app/communication/interrogation" replace />} />

                                {/* Mobile Device Routes - removed, redirect to Sacred Timeline */}
                                <Route path="mobile/iphone" element={<Navigate to="/app/monitoring/sacred-timeline" replace />} />
                                <Route path="mobile/android" element={<Navigate to="/app/monitoring/sacred-timeline" replace />} />

                                {/* System Routes */}
                                <Route path="system/configuration" element={<Configuration />} />
                                <Route path="system/debug" element={<DebugSettings />} />
                                <Route path="system/unauthorized" element={<Unauthorized />} />

                                {/* Legacy redirects for backward compatibility */}
                                <Route path="system/file-browser" element={<Navigate to="/app/monitoring/file-browser" replace />} />
                                <Route path="punishment/file-block" element={<Navigate to="/app/monitoring/file-browser" replace />} />

                                {/* Default route - person-centric Home */}
                                <Route path="" element={<Navigate to="/app/home" replace />} />
                                {/* Unknown in-app paths get a real 404 (keeps the
                                    shell/sidebar) instead of a silent bounce to Home. */}
                                <Route path="*" element={<NotFound />} />
                              </Route>
                            </Route>
                          </Routes>
                        </div>
                        </ScreenShareProvider>
                        </PeopleProvider>
                      </SocketProvider>
                    </KeywordProvider>
                    </AdvisorQueueProvider>
                    </AIModeProvider>
                  </WebRTCProvider>
                }
              />
            }
          />

          {/* Root redirects */}
          <Route path="/" element={<Navigate to="/app/home" replace />} />
          <Route path="*" element={<Navigate to="/user/login" replace />} />
        </Routes>
      </Suspense>
      </TooltipProvider>
    );
  }
}

const mapStateToProps = (state) => {
  // Provide defaults in case settings doesn't exist
  const settings = state.settings || {};
  const { locale = 'en' } = settings;
  return { locale };
};

export default connect(mapStateToProps, {})(App);
